import { makeStyles } from '@material-ui/core';
import InvestorIcon from '@material-ui/icons/People';
import React, { useMemo, useEffect, useState } from 'react';
import {
  Datagrid,
  List,
  ReferenceManyField,
  Responsive,
  TextInput,
  SelectInput,
  SimpleList,
  TextField,
  TopToolbar,
  Pagination,
  useGetList,
  useQuery,
  Loading,
  useListContext
} from 'react-admin';

import BulkActionDeleteButton from '../../core/components/BulkActionDeleteButton';
import LinkField from '../../core/components/LinkField';
import Empty from '../../core/components/list/Empty';
import Filter from '../../core/components/list/StyledFilter';
import StatusSelectField from '../../core/components/StatusSelectField';
import AutocompleteInput from '../../core/components/forked/input/AutocompleteInput';
import {
  predictiveSearchFormatter,
  predictiveSearchParser
} from '../../core/utils';
import FundInvestorDatagrid from './FundInvestorDatagrid';
import { investorStatus } from './InvestorLookups';
import ListTitleBar from './importinvestors/ExcelInvestorImportTitleBarWithDropbox';
import TagsField from '../../core/components/TagsField';
import TagFilter from '../../core/components/inputs/TagFilter';

const useStyles = makeStyles({
  expandOnlyDataGridHead: {
    '& tr': {
      '& th:nth-of-type(1)': {
        width: '14px'
      }
    }
  },
  expandDataGridHead: {
    '& tr': {
      '& th:nth-of-type(1)': {
        width: '14px'
      },
      '& th:nth-of-type(2)': {
        width: '24px'
      }
    }
  },
  expandIconCell: {
    width: '14px',
    '& div': {
      width: '14px',
      padding: '0'
    },
    '& div:hover ': {
      backgroundColor: 'transparent'
    }
  },
  statusColumnStyle: {
    width: '68px'
  },
  root: {
    marginRight: 'auto'
  }
});

const useshowStyles = makeStyles({
  root: {
    padding: '16px 16px 10px 16px'
  }
});

const Actions = ({
  displayedFilters,
  filters,
  filterValues,
  resource,
  showFilter
}) => {
  const classes = useStyles();
  return (
    <TopToolbar classes={{ root: classes.root }}>
      {filters &&
        React.cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: 'button'
        })}
    </TopToolbar>
  );
};

const InvestorFilters = ({
  filterValues,
  setAppliedFilters,
  tagsList,
  selectedTags,
  setSelectedTags,
  investorTagsCount,
  ...props
}) => {
  const { data } = useQuery({
    type: 'getList',
    resource: 'fundfamilies',
    payload: {
      filter: {
        orderBy: 'name',
        orderDirection: 'ASC'
      }
    }
  });

  useEffect(() => {
    // Hack to reset filter when a tag is deleted while it was selected previously for filtering the list
    filterValues.investorTagId?.forEach(tagId => {
      const isTagInDB = Object.values(tagsList).some(
        tagChoice => tagChoice.id === tagId
      );
      if (tagsList.length === 0 || !isTagInDB) {
        delete filterValues.investorTagId;
        setSelectedTags([]);
      }
    });
    if (filterValues) {
      setAppliedFilters(filterValues);
      if (filterValues.investorTagId) {
        setSelectedTags(filterValues.investorTagId);
      }
    }
  }, [filterValues, tagsList, setAppliedFilters, setSelectedTags]);

  return (
    <Filter variant="standard" {...props}>
      <TextInput
        label="Search by Investor Name"
        source="name"
        alwaysOn
        parse={predictiveSearchParser}
        format={predictiveSearchFormatter}
        options={{ autoComplete: 'off' }}
      />
      {data && data.length > 0 && (
        <AutocompleteInput
          source="fundFamilyId"
          label="Fund Families"
          alwaysOn
          optionText="name"
          autoComplete="off"
          showArrowDown={true}
          clientSideFilterOnly={true}
          choices={data}
          emptyText="All Fund Families"
          placeholder="All Fund Families"
        />
      )}
      {investorTagsCount > 0 && (
        <TagFilter
          alwaysOn
          source="investorTagId"
          tagsList={tagsList}
          selectedTags={selectedTags}
          onChange={e => setSelectedTags(e)}
          displayTagFilter={true}
          onChangeFundInv={() => null}
          displayFundInvTagFilter={false}
          isListFilter={true}
          selectLabel="Investor Tags"
          topLabel="Investor Tags"
          filterValues={filterValues}
        />
      )}
      <TextInput
        label="Investor ID"
        source="id"
        alwaysOn
        allowEmpty
        parse={predictiveSearchParser}
        format={predictiveSearchFormatter}
        options={{ autoComplete: 'off' }}
      />
      <SelectInput
        label="Status"
        source="status"
        choices={investorStatus}
        allowEmpty
      />
    </Filter>
  );
};

const InvestorShow = props => {
  const classes = useshowStyles();
  const { handleFundClick, permissions, ...rest } = props;

  const { data } = useGetList('fundinvestortags', null, null);
  const fundInvTagsList = Object.values(data);

  return (
    <div className={classes.root}>
      <ReferenceManyField
        label=""
        reference="fundinvestors"
        target="investorId"
        sort={{ field: 'fundName', order: 'ASC' }}
        filter={{ returnFundInvestorTags: 'true' }}
        {...rest}
      >
        <FundInvestorDatagrid
          handleFundClick={handleFundClick}
          expand={true}
          permissions={permissions}
          fundInvTagsList={fundInvTagsList}
        />
      </ReferenceManyField>
    </div>
  );
};

const InvestorsTitle = props => {
  return (
    <ListTitleBar
      type="Investors"
      title="Create New Investor"
      createUrl="#/investors/create"
      icon={<InvestorIcon />}
      hasCreatePermission={props.hasCreatePermission}
      hasImportInvestorExcelPermission={props.hasImportInvestorExcelPermission}
      hasExportInvestorsExcelPermission={
        props.hasExportInvestorsExcelPermission
      }
      hasImportWireInstructionExcelPermission={
        props.hasImportWireInstructionExcelPermission
      }
      hasCreateWireInstructionPermission={
        props.hasCreateWireInstructionPermission
      }
      filters={props.filters}
      selectedIds={props.selectedIds}
    />
  );
};

const InvestorList = props => {
  const handleFundClick = fundInvestorId => {
    const fundId = fundInvestorId.split(':')[0];
    return `/funds/${fundId}`;
  };
  const classes = useStyles();
  const {
    permissions = [],
    hasCreatePermission,
    hasEditPermission,
    hasDeletePermission,
    ...rest
  } = props;

  const [appliedFilters, setAppliedFilters] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  const { hasImportInvestorExcelPermission } = useMemo(() => {
    return {
      hasImportInvestorExcelPermission: props.permissions
        ? props.permissions.includes('AUTH_IMPORT_INVESTOR_EXCEL')
        : false
    };
  }, [props.permissions]);

  const { hasExportInvestorsExcelPermission } = useMemo(() => {
    return {
      hasExportInvestorsExcelPermission: props.permissions
        ? props.permissions.includes('AUTH_EXPORT_INVESTORS')
        : false
    };
  }, [props.permissions]);

  const { hasImportWireInstructionExcelPermission } = useMemo(() => {
    return {
      hasImportWireInstructionExcelPermission: props.permissions
        ? props.permissions.includes('AUTH_IMPORT_WIRE_INSTRUCTION_EXCEL')
        : false
    };
  }, [props.permissions]);

  const { hasCreateWireInstructionPermission } = useMemo(() => {
    return {
      hasCreateWireInstructionPermission: props.permissions
        ? props.permissions.includes('AUTH_CREATE_WIRE_INSTRUCTION')
        : false
    };
  }, [props.permissions]);

  // Fetch all investortags. If there is at least one, we need to display the "Investor Tags" column.
  const {
    data: tagsList,
    loading: investorTagLoading,
    total: investorTagsCount
  } = useGetList('investortags', null, null);

  // Wait for the GET /v1/investortags response before displaying the grid.
  if (investorTagLoading) {
    return <Loading />;
  } else {
    return (
      <List
        {...rest}
        filters={
          <InvestorFilters
            setAppliedFilters={setAppliedFilters}
            tagsList={Object.values(tagsList ? tagsList : [{}])}
            selectedTags={selectedTags}
            setSelectedTags={setSelectedTags}
            investorTagsCount={investorTagsCount}
          />
        }
        title={
          <InvestorsTitle
            hasImportInvestorExcelPermission={hasImportInvestorExcelPermission}
            hasExportInvestorsExcelPermission={
              hasExportInvestorsExcelPermission
            }
            hasImportWireInstructionExcelPermission={
              hasImportWireInstructionExcelPermission
            }
            hasCreateWireInstructionPermission={
              hasCreateWireInstructionPermission
            }
            hasCreatePermission={hasCreatePermission}
            filters={appliedFilters}
            selectedIds={selectedIds}
          />
        }
        exporter={false}
        sort={{ field: 'name', order: 'ASC' }}
        perPage={20}
        pagination={<Pagination rowsPerPageOptions={[20, 25, 30]} />}
        filter={{
          orphanVisibility: 'ALL_VISIBLE',
          returnInvestorTags: investorTagsCount > 0
        }}
        bulkActionButtons={hasDeletePermission && <BulkActionDeleteButton />}
        actions={<Actions />}
        empty={<Empty />}
        syncWithLocation
      >
        <Responsive
          small={
            <SimpleList
              primaryText={record => (record?.name ? record.name : '')}
              secondaryText={record => (record?.id ? record.id : '')}
              tertiaryText={() => ''}
            />
          }
          medium={
            <DatagridWithListContext
              hasDeletePermission={hasDeletePermission}
              handleFundClick={handleFundClick}
              investorTagsCount={investorTagsCount}
              permissions={permissions}
              classes={classes}
              setSelectedIds={setSelectedIds}
              {...rest}
            />
          }
        />
      </List>
    );
  }
};

const DatagridWithListContext = props => {
  const {
    hasDeletePermission,
    handleFundClick,
    investorTagsCount,
    permissions,
    classes,
    setSelectedIds,
    ...dataGridProps
  } = props;
  const { selectedIds } = useListContext(props);

  useEffect(() => {
    setSelectedIds(selectedIds);
  }, [selectedIds, setSelectedIds]);

  return (
    <>
      <Datagrid
        {...dataGridProps}
        classes={{
          thead: hasDeletePermission
            ? classes.expandDataGridHead
            : classes.expandOnlyDataGridHead,
          expandIconCell: classes.expandIconCell
        }}
        expand={
          <InvestorShow
            handleFundClick={handleFundClick}
            permissions={permissions}
          />
        }
      >
        <LinkField label="Investor Name" source="name" id="Investor Name" />
        <TextField label="A.K.A." source="aka" id="A.K.A" />
        <TextField label="Investor ID" source="id" id="Investor ID" />
        {investorTagsCount > 0 && (
          <TagsField
            label="Investor Tags"
            source="investortags"
            tagType="investorTags"
            sortable={false}
          />
        )}
        <StatusSelectField
          id="Status"
          label="Status"
          source="status"
          choices={investorStatus}
          headerClassName={classes.statusColumnStyle}
        />
      </Datagrid>
    </>
  );
};

export default InvestorList;
